import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa6';
import * as adminRolesService from '../../data/services/adminRolesService';
import { getAllOrgDomains } from '../../../../shared/utils/organizationDomainUtil';
import './AdminRoleFormScreen.css';

const AdminRoleFormScreen = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    roleName: '',
    description: '',
    orgId: null, // null = global
  });
  const [orgDomains, setOrgDomains] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        setLoadingOrgs(true);
        const orgs = await getAllOrgDomains();
        setOrgDomains(orgs);
      } catch (error) {
        console.error('Error loading organizations:', error);
      } finally {
        setLoadingOrgs(false);
      }
    };
    loadOrganizations();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.roleName || !formData.roleName.trim()) {
      newErrors.roleName = 'Role name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    setSubmitting(true);
    try {
      const role = await adminRolesService.createRole({
        roleName: formData.roleName.trim(),
        description: formData.description.trim() || null,
        orgId: formData.orgId || null,
      });
      
      // Navigate to role detail screen
      navigate(`/admin/roles/${role.roleId}`);
    } catch (error) {
      console.error('Error creating role:', error);
      alert(error.message || 'Failed to create role. Please try again.');
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/roles');
  };

  return (
    <div className="admin-role-form-screen">
      <div className="admin-role-form-container">
        <div className="admin-role-form-header">
          <button
            className="back-button"
            onClick={handleCancel}
            aria-label="Back to Roles"
          >
            <FaArrowLeft className="back-icon" />
          </button>
          <h1 className="admin-role-form-title">Add New Role</h1>
        </div>

        <form onSubmit={handleSubmit} className="admin-role-form">
          <div className="form-group">
            <label htmlFor="roleName" className="form-label">
              Role Name <span className="required">*</span>
            </label>
            <input
              id="roleName"
              type="text"
              className={`form-input ${errors.roleName ? 'error' : ''}`}
              value={formData.roleName}
              onChange={(e) => handleInputChange('roleName', e.target.value)}
              placeholder="e.g., Product Manager"
              disabled={submitting}
            />
            {errors.roleName && (
              <span className="error-message">{errors.roleName}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="description" className="form-label">
              Description
            </label>
            <textarea
              id="description"
              className="form-textarea"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Enter a description for this role..."
              rows={4}
              disabled={submitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="orgId" className="form-label">
              Organization Assignment
            </label>
            <select
              id="orgId"
              className="form-input"
              value={formData.orgId || ''}
              onChange={(e) => handleInputChange('orgId', e.target.value === '' ? null : e.target.value)}
              disabled={loadingOrgs || submitting}
              style={{ cursor: loadingOrgs ? 'wait' : 'pointer' }}
            >
              <option value="">Global (all organizations)</option>
              {orgDomains.map((org) => (
                <option key={org.orgId} value={org.orgId}>
                  {org.domain}
                </option>
              ))}
            </select>
            <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.25rem' }}>
              {formData.orgId === null 
                ? 'Global roles are visible to all organizations. Users from any organization can select this role.'
                : `This role will be specific to ${orgDomains.find(o => o.orgId === formData.orgId)?.domain || formData.orgId}. Only users from this organization will see it.`
              }
            </p>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={submitting}
            >
              {submitting ? 'Creating...' : 'Create Role'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminRoleFormScreen;
